/**
 * ChipsSelector, pure half.
 *
 * Moved verbatim from components/FlowBuilder/flow-components/components/ChipsSelectorComponent.js. Runs unchanged in the
 * browser and, via the flow-core bundle, inside GraalJS on the backend, so it
 * must never reach for React, the DOM, Node APIs, or the network.
 */

import { reservedFieldNameErrors } from '../fieldName';
import { DATA_SOURCE_MODE, DATA_SOURCE_REF, defaultBindingProps, isDynamic, dataSourceForWhatsApp, readLoadedDataSource, validateBinding } from '../dataSourceBinding';

export const ChipsSelectorCore = {
  type: 'ChipsSelector',
  name: 'Chips Selector',
  icon: '🏷️',
  description: 'Pick one or more options shown as compact tappable chips.',
  category: 'selection',
  maxPerScreen: null,
  getDefaultProperties() {
    return {
      name: 'chips_selector',
      label: 'Select options',
      description: '',
      'data-source': [
        { id: 'chip1', title: 'Option 1', enabled: true },
        { id: 'chip2', title: 'Option 2', enabled: true },
        { id: 'chip3', title: 'Option 3', enabled: true },
      ],
      ...defaultBindingProps(),
      'min-selected-items': '',
      'max-selected-items': '',
      required: false,
      enabled: true,
      visible: true,
      'init-value': [],
      'error-message': '',
    };
  },
  getPropertySchema() {
    return {
      name: {
        type: 'string',
        label: 'Field Name',
        required: true,
      },
      label: {
        type: 'string',
        label: 'Label',
        required: true,
      },
      description: {
        type: 'string',
        label: 'Description',
        required: false,
      },
      'data-source': {
        type: 'array',
        label: 'Chips',
        required: true,
        itemSchema: { id: 'string', title: 'string', enabled: 'boolean' },
      },
      'min-selected-items': {
        type: 'number',
        label: 'Min Selected',
        required: false,
      },
      'max-selected-items': {
        type: 'number',
        label: 'Max Selected',
        required: false,
      },
      required: {
        type: 'boolean',
        label: 'Required',
        required: false,
      },
      enabled: {
        type: 'boolean',
        label: 'Enabled',
        required: false,
      },
      visible: {
        type: 'boolean',
        label: 'Visible',
        required: false,
      },
      'init-value': {
        type: 'array',
        label: 'Initial Selection',
        required: false,
      },
      'error-message': {
        type: 'string',
        label: 'Error Message',
        required: false,
      },
    };
  },
  validateProperties(properties) {
    var errors = [];
    var warnings = [];

    if (!properties.name || properties.name.trim().length === 0) {
      errors.push('Field name is required');
    }

    if (properties.name && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(properties.name)) {
      errors.push('Field name must start with letter or underscore');
    }

    errors.push(...reservedFieldNameErrors(properties.name));

    if (!properties.label || properties.label.trim().length === 0) {
      errors.push('Label is required');
    }

    if (properties.label && properties.label.length > 80) {
      errors.push('Label must be 80 characters or less');
    }

    if (properties.description && properties.description.length > 300) {
      errors.push('Description must be 300 characters or less');
    }

    if (isDynamic(properties)) {
      var binding = validateBinding(properties);
      errors.push(...binding.errors);
      warnings.push(...binding.warnings);
    } else {
      if (!properties['data-source'] || properties['data-source'].length < 2) {
        errors.push('At least 2 chips are required');
      }

      if (properties['data-source'] && properties['data-source'].length > 20) {
        errors.push('Maximum 20 chips allowed');
      }

      if (properties['data-source']) {
        var ids = new Set();
        properties['data-source'].forEach((chip, index) => {
          if (!chip.id) {
            errors.push(`Chip ${index + 1} is missing an ID`);
          } else if (ids.has(chip.id)) {
            errors.push(`Duplicate chip ID: ${chip.id}`);
          } else {
            ids.add(chip.id);
          }

          if (!chip.title) {
            errors.push(`Chip ${index + 1} is missing a title`);
          }
        });
      }
    }

    if (properties['min-selected-items'] && properties['max-selected-items']) {
      var minVal = parseInt(properties['min-selected-items'], 10);
      var maxVal = parseInt(properties['max-selected-items'], 10);
      if (!isNaN(minVal) && !isNaN(maxVal) && minVal > maxVal) {
        errors.push('Min selected items cannot be greater than max selected items');
      }
    }

    return { errors, warnings };
  },
  transformToWhatsApp(properties) {
    var staticDataSource = (properties['data-source'] || []).map((chip) => {
      var chipResult = { id: chip.id, title: chip.title };
      if (chip.enabled === false) {
        chipResult.enabled = false;
      }
      return chipResult;
    });

    var result = {
      type: 'ChipsSelector',
      name: properties.name,
      label: properties.label,
      'data-source': dataSourceForWhatsApp(properties, staticDataSource),
    };

    if (properties.description && properties.description.trim()) {
      result.description = properties.description;
    }

    if (properties['min-selected-items'] !== '' && properties['min-selected-items'] !== undefined) {
      var minVal = parseInt(properties['min-selected-items'], 10);
      if (!isNaN(minVal)) {
        result['min-selected-items'] = minVal;
      }
    }

    if (properties['max-selected-items'] !== '' && properties['max-selected-items'] !== undefined) {
      var maxVal = parseInt(properties['max-selected-items'], 10);
      if (!isNaN(maxVal)) {
        result['max-selected-items'] = maxVal;
      }
    }

    if (properties.required) {
      result.required = true;
    }

    if (properties['init-value'] && properties['init-value'].length > 0) {
      result['init-value'] = properties['init-value'];
    }

    if (properties['error-message'] && properties['error-message'].trim()) {
      result['error-message'] = properties['error-message'];
    }

    if (properties.enabled === false) {
      result.enabled = false;
    }

    if (properties.visible === false) {
      result.visible = false;
    }

    return result;
  },
  transformFromWhatsApp(whatsappComponent) {
    var loaded = readLoadedDataSource(whatsappComponent['data-source']);
    return {
      name: whatsappComponent.name,
      label: whatsappComponent.label,
      description: whatsappComponent.description || '',
      'data-source': loaded.array.map((chip) => ({
        id: chip.id,
        title: chip.title,
        enabled: chip.enabled !== false,
      })),
      [DATA_SOURCE_MODE]: loaded.mode,
      [DATA_SOURCE_REF]: loaded.ref,
      'min-selected-items': whatsappComponent['min-selected-items'] || '',
      'max-selected-items': whatsappComponent['max-selected-items'] || '',
      required: whatsappComponent.required || false,
      enabled: whatsappComponent.enabled !== false,
      visible: whatsappComponent.visible !== false,
      'init-value': whatsappComponent['init-value'] || [],
      'error-message': whatsappComponent['error-message'] || '',
    };
  },
};
